import { useDialog } from '@ics-portal/guardian';
import { type FormEvent, type ReactNode, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    NewComponentRequestForm,
    type NewComponentRequestFormValue,
} from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/components/NewComponentRequestForm';
import { useNewComponentRequestActions } from '@hooks/NewComponentRequests/useNewComponentRequestActions';
import { getRequestFormDefaultValues } from '@pages/notification-components/utils/getRequestFormDefaultValues';
import { type RequestRow } from '../columns';

interface EditRequestDialogProps {
    request: RequestRow;
}

export function EditRequestDialog({
    request,
}: EditRequestDialogProps): ReactNode {
    const { close, handleSubmit: bindDialogSubmit } = useDialog();
    const { update } = useNewComponentRequestActions();

    const form = useForm<NewComponentRequestFormValue>({
        defaultValues: getRequestFormDefaultValues(request),
    });
    const { handleSubmit } = form;

    const submitHandler = useCallback(
        async (
            formValue: Partial<NewComponentRequestFormValue>,
        ): Promise<void> => {
            await update(formValue);
            close();
        },
        [close, update],
    );

    useEffect(() => {
        bindDialogSubmit(() => handleSubmit(submitHandler)());
        // eslint-disable-next-line react/exhaustive-deps -- dialog provider exposes non-memoized handlers; register once on mount
    }, []);

    const onFormSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            void handleSubmit(submitHandler)(event);
        },
        [handleSubmit, submitHandler],
    );

    return (
        <form id="editComponentRequestForm" noValidate onSubmit={onFormSubmit}>
            <NewComponentRequestForm form={form} />
        </form>
    );
}
